import { multicall, simulateContract } from '@wagmi/core'
import { ConfigBuilderAbi } from 'abis/ConfigBuilderAbi'
import { IndexV2Abi } from 'abis/IndexV2'
import { WAD, ZERO_EX_AFFILIATE_AVAX_ADDRESS, ZERO_EX_AFFILIATE_BASE_ADDRESS } from 'app-constants'
import Big from 'big.js'
import { atom } from 'jotai'
import { ARTXAI, JMX } from 'products'
import { getPricesStore } from 'services/price-history/utils'
import { type AnatomyWithExtra, defaultIndexAnatomy } from 'services/query-v2/anatomy'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { QUERY_KEY_OUTPUT_AMOUNT } from 'services/query-v2/keys'
import { ZeroExAggregator } from 'services/query-v2/product-v2/aggregators/0x-aggregators'
import { deployments } from 'services/query-v2/product-v2/deployments'
import { sumBalancesByCurrency } from 'services/query-v2/product-v2/redeem/utils'
import { getRedstonePayload } from 'services/query-v2/product-v2/redstone-utils'
import { fromA160U96 } from 'services/query-v2/product-v2/utils'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { wagmiConfig } from 'services/wallet/chains'
import { convertAmountToWei, formatWeiToAmount, objToQueryString } from 'utils'
import {
  type Address,
  encodeAbiParameters,
  isAddressEqual,
  keccak256,
  padHex,
  parseAbiParameters,
  toHex,
  zeroAddress,
} from 'viem'
import {
  type OutputAmountResponse,
  type OutputRequestAtoms,
  type PriceParams,
  type RawQuotesResponse,
  defaultPriceParams,
} from './model'
import { NetworkType, zeroExWrapperUrl } from 'abis/set/sets'
import { notifyTransactionLiquidityError } from 'components'

const parseV2Output = (params: PriceParams, amount: Big): OutputAmountResponse => {
  return {
    chainId: params.chainId,
    currency: params.sellCurrency,
    buyAmount: amount,
  }
}

const indexV2OutputAmountQuery = async <T>({
  queryKey: [, params, anatomy],
}: {
  queryKey: [string, PriceParams, AnatomyWithExtra]
}) => {
  const defaultUser = '0x000000000000000000000000000000000000dead' as Address
  const defaultAmountResponse: OutputAmountResponse = {
    chainId: params.chainId,
    currency: params.sellCurrency,
    buyAmount: Big(0),
  }

  if (params.sellAmount.eq(0)) {
    return defaultAmountResponse as T
  }

  if (
    params.sellCurrency.type === 'index-v2' &&
    isAddressEqual(params.sellCurrency.address, ARTXAI.address)
  ) {
    const aggregator = ZeroExAggregator.builder.build()
    const config = {
      shared: {
        AUMDilutionPerSecond: BigInt(anatomy.config.AUMDilutionPerSecond),
        useCustomAUMFee: anatomy.config.useCustomAUMFee,
        metadata: anatomy.config.metadata as Address,
      },
      depositFee: {
        BPs: +anatomy.config.depositFeeInBP,
        useCustomCallback: anatomy.config.depositCustomCallback,
      },
      redemptionFee: {
        BPs: +anatomy.config.redemptionFeeInBP,
        useCustomCallback: anatomy.config.redemptionCustomCallback,
      },
    }
    const homeChain = params.sellCurrency.chainId

    const { ConfigBuilder, Messenger: HomeMessenger } =
      deployments[homeChain][anatomy.productAddress]

    const amount = BigInt(
      convertAmountToWei(params.sellAmount, params.sellCurrency.decimals).toFixed(),
    )

    const contract = {
      address: ConfigBuilder,
      abi: ConfigBuilderAbi,
    }

    const [[, redeemConfig], { staticPriceOracle }, reserve] = await multicall(
      //totalSupply
      wagmiConfig,
      {
        contracts: [
          {
            ...contract,
            functionName: 'configs',
            args: [config],
          },
          {
            ...contract,
            functionName: 'getState',
          },
          {
            address: anatomy.productAddress,
            abi: IndexV2Abi,
            functionName: 'reserve',
          },
          {
            address: anatomy.productAddress,
            abi: IndexV2Abi,
            functionName: 'totalSupply',
          },
        ],
        allowFailure: false,
      },
    )

    // if (amount <= totalSupply) {
    const redeemParams = {
      config: redeemConfig,
      owner: defaultUser,
      shares: amount,
      payload: await getRedstonePayload.fromContract(staticPriceOracle),
    }

    const { result } = await simulateContract(wagmiConfig, {
      account: HomeMessenger,
      address: anatomy.productAddress,
      abi: IndexV2Abi,
      functionName: 'redeem',
      args: [redeemParams, defaultUser, defaultUser],
      stateOverride: [
        {
          address: anatomy.productAddress,
          stateDiff: [
            {
              slot: keccak256(
                encodeAbiParameters(parseAbiParameters('address user, uint slot'), [
                  defaultUser,
                  BigInt(15),
                ]),
              ),
              value: padHex(toHex(amount), { size: 32 }),
            },
          ],
        },
      ],
    })
    const amounts = []
    if (result.accountReserveRedeemed !== 0n) {
      amounts.push({ currency: reserve, balance: result.accountReserveRedeemed })
    }
    if (result.accountKRedeemed !== 0n) {
      amounts.push(
        ...redeemConfig.homeCurrencies.map((c) => {
          const { address: currency, value } = fromA160U96(c)

          const amount = (value * result.accountKRedeemed) / WAD
          return { currency: currency.toLowerCase(), balance: amount }
        }),
      )
    }

    const outputAmount = (
      await Promise.all(
        sumBalancesByCurrency(amounts.filter((a) => a.balance > 0n)).map(
          async ({ currency, balance }) => {
            if (isAddressEqual(currency, params.buyCurrency.address)) return balance
            const quote = await aggregator.quote(
              params.sellCurrency.chainId,
              currency,
              params.buyCurrency.address,
              balance,
              deployments[params.sellCurrency.chainId][ARTXAI.address].DefaultEscrow,
            )
            return quote.output
          },
        ),
      )
    ).reduce((result, amount) => result + amount, 0n)

    return parseV2Output(params, formatWeiToAmount(outputAmount, params.buyCurrency.decimals)) as T
    // } else {
    // console.log('previous logic')
    // do multichain logic
    // }
  }
  if (isAddressEqual(params.productAddress, JMX.address)) {
    const amount = convertAmountToWei(params.sellAmount, params.sellCurrency.decimals)
    const adjustedAmount = amount.gt(0)
      ? amount
      : convertAmountToWei(Big('1'), params.sellCurrency.decimals)

    const urlParams = {
      chainId: params.chainId,
      sellToken: isAddressEqual(params.sellCurrency.address, zeroAddress)
        ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
        : params.sellCurrency.address,
      buyToken: isAddressEqual(params.buyCurrency.address, zeroAddress)
        ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
        : params.buyCurrency.address,
      sellAmount: adjustedAmount.toFixed(),
      affiliateAddress: params.affiliateAddress,
      slippageBps: params.slippagePercentage * 1000,
      taker: params.userAddress,
      txOrigin: params.userAddress,
      swapFeeToken: isAddressEqual(params.sellCurrency.address, zeroAddress)
        ? '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
        : params.sellCurrency.address,
      swapFeeBps: 30,
      swapFeeRecipient:
        params.chainId === NetworkType.Avalanche
          ? ZERO_EX_AFFILIATE_AVAX_ADDRESS
          : params.chainId === NetworkType.Base
            ? ZERO_EX_AFFILIATE_BASE_ADDRESS
            : undefined,
    }

    const url = zeroExWrapperUrl
    const response = await fetch(
      `${url}/swap/allowance-holder/quote?${objToQueryString(urlParams)}`,
    )
    const json = (await response.json()) as RawQuotesResponse | { liquidityAvailable: false }
    if (!(json as { liquidityAvailable: false }).liquidityAvailable) {
      notifyTransactionLiquidityError(
        `${params.sellCurrency.address}-${params.sellAmount.toString()}`,
      )
      return defaultAmountResponse
    }
    return parseV2Output(
      params,
      formatWeiToAmount((json as RawQuotesResponse).buyAmount, params.buyCurrency.decimals),
    ) as T
  }
  const value = params.sellValue

  const prices = getPricesStore()
  const key = `${params.sellCurrency.chainId}:${params.sellCurrency.address}`
  const productPrices = Object.values(prices[key])
  const productPrice = Big(productPrices[productPrices.length - 1])

  return parseV2Output(params, value.div(productPrice)) as T
}

export const indexV2OutputAmountAtomsFactory = () => {
  const paramsAtom = atom<PriceParams>(defaultPriceParams)
  const indexAnatomyAtom = atom<AnatomyWithExtra>(defaultIndexAnatomy)

  const mainProperties = createAtomsWithQuery<
    OutputAmountResponse,
    PriceParams,
    AnatomyWithExtra,
    null,
    [string, PriceParams, AnatomyWithExtra]
  >(
    { first: paramsAtom, second: indexAnatomyAtom },
    QUERY_KEY_OUTPUT_AMOUNT,
    indexV2OutputAmountQuery<OutputAmountResponse>,
    DEFAULT_REFETCH_INTERVAL,
  )

  return {
    params: paramsAtom,
    indexAnatomy: indexAnatomyAtom,
    ...mainProperties,
  } as OutputRequestAtoms
}
